/* eslint-disable @typescript-eslint/camelcase */
import { AxiosResponse } from 'axios'
import {
  getToRestApiGateway,
  postToRestApiGateway,
  putToRestApiGateway,
  deleteToRestApiGateway,
} from '@/lib/api-connect'
import type {
  GetSpecialSupportPlanListResponse,
  GetSpecialSupportPlanListByChildIdResponse,
  GetSpecialSupportPlanResponse,
  PostSpecialSupportPlanResponse,
  PutSpecialSupportPlanResponse,
  DeleteSpecialSupportPlanResponse,
  PostSpecialSupportPlanParams,
  PutSpecialSupportPlanParams,
  PostGenerateSupportPolicyResponse,
  PostGenerateSupportItemsResponse,
} from '@/lib/api/types/specialSupportPlan'
import type {
  Params as GenerateSupportPolicyParams,
  PramsSupportItem,
} from '@/components/organisms/specialSupportPlan/details/AI/types'

/** 事業所に紐づく専門的支援計画を取得するAPI */
export const getSpecialSupportPlanList = async (
  officeId: string
): Promise<AxiosResponse<GetSpecialSupportPlanListResponse>> => {
  return getToRestApiGateway('specialized_support_plan/children', {
    office_id: officeId,
  })
}

/** 児童に紐づく専門的支援計画を取得するAPI */
export const getSpecialSupportPlanListByChildId = async (
  childId: string,
  officeId: string
): Promise<AxiosResponse<GetSpecialSupportPlanListByChildIdResponse>> => {
  return getToRestApiGateway(
    `specialized_support_plan/children/${childId}/plans`,
    {
      office_id: officeId,
    }
  )
}

/** planIdから専門的支援計画を取得するAPI */
export const getSpecialSupportPlanListByPlanId = async ({
  planId,
  childId,
  officeId,
}: {
  planId: string
  childId: string
  officeId: string
}): Promise<AxiosResponse<GetSpecialSupportPlanResponse>> => {
  return getToRestApiGateway(
    `specialized_support_plan/children/${childId}/plans/${planId}`,
    {
      office_id: officeId,
    }
  )
}

/** 専門的支援計画を新規作成するAPI */
export const postSpecialSupportPlan = async ({
  childId,
  ...planParams
}: { childId: string } & PostSpecialSupportPlanParams): Promise<
  AxiosResponse<PostSpecialSupportPlanResponse>
> => {
  return postToRestApiGateway(
    `specialized_support_plan/children/${childId}/plans`,
    planParams
  )
}

/** 専門的支援計画を更新するAPI */
export const putSpecialSupportPlan = async ({
  childId,
  planId,
  ...planParams
}: { childId: string; planId: string } & PutSpecialSupportPlanParams): Promise<
  AxiosResponse<PutSpecialSupportPlanResponse>
> => {
  return putToRestApiGateway(
    `specialized_support_plan/children/${childId}/plans/${planId}`,
    planParams
  )
}

/** 専門的支援計画を削除するAPI */
export const deleteSpecialSupportPlan = async ({
  officeId,
  childId,
  planId,
}: {
  officeId: string
  childId: string
  planId: string
}): Promise<AxiosResponse<DeleteSpecialSupportPlanResponse>> => {
  return deleteToRestApiGateway(
    `specialized_support_plan/children/${childId}/plans/${planId}`,
    {
      office_id: officeId,
    }
  )
}

/** 専門的支援計画から支援方針を生成するAPI */
export const postGenerateSupportPolicy = async ({
  officeId,
  childId,
  keyword,
  assessmentResult,
}: GenerateSupportPolicyParams): Promise<
  AxiosResponse<PostGenerateSupportPolicyResponse>
> => {
  const params = {
    officeId,
    keyword,
    assessment: assessmentResult,
  }
  return postToRestApiGateway(
    `specialized_support_plan/children/${childId}/generate_support_policy`,
    params
  )
}

/** 専門的支援計画の支援項目を生成するAPI */
export const postGenerateSupportItems = async ({
  officeId,
  childId,
  keyword,
  assessmentResult,
  prioritySupportArea,
  totalGoal,
  summaryContent,
}: PramsSupportItem): Promise<
  AxiosResponse<PostGenerateSupportItemsResponse>
> => {
  const params = {
    officeId,
    keyword,
    assessment: assessmentResult,
    prioritySupportArea,
    totalGoal,
    summaryContent,
  }
  return postToRestApiGateway(
    `specialized_support_plan/children/${childId}/generate_support_items`,
    params
  )
}
